import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon, UsersIcon, XMarkIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { getMembers, inviteMember, getMemberClients, MemberClient, resetMemberPin } from '@services/admin.service';
import { MemberUser } from '@utilities/types';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const Members: FC = () => {
    const [members, setMembers] = useState<MemberUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination & search
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Invite modal state
    const [showModal, setShowModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteClientCode, setInviteClientCode] = useState('');
    const [inviting, setInviting] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
    const [resettingPinId, setResettingPinId] = useState<string | null>(null);

    // Clients modal state
    const [selectedMember, setSelectedMember] = useState<MemberUser | null>(null);
    const [memberClients, setMemberClients] = useState<MemberClient[]>([]);
    const [loadingClients, setLoadingClients] = useState(false);

    const fetchMembers = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getMembers();
            setMembers(res.data);
        } catch (err: any) {
            setError(err.message || 'Erreur lors du chargement des membres');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    // Filtered members
    const filtered = useMemo(() => {
        if (!search.trim()) return members;
        const q = search.toLowerCase();
        return members.filter((m) => {
            const email = m.email?.toLowerCase() || '';
            const clientCode = m.clientCode?.toLowerCase() || '';
            const firstName = m.firstName?.toLowerCase() || '';
            const lastName = m.lastName?.toLowerCase() || '';
            const fullName = `${firstName} ${lastName}`;
            const date = new Date(m.createdAt).toLocaleDateString('fr-FR');
            return email.includes(q) || clientCode.includes(q) || firstName.includes(q) || lastName.includes(q) || fullName.includes(q) || date.includes(q);
        });
    }, [members, search]);

    // Reset page on search/rowsPerPage change
    useEffect(() => { setPage(1); }, [search, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
    const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const handleInvite = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        setInviting(true);
        setInviteError(null);
        setInviteSuccess(null);

        try {
            await inviteMember(inviteEmail, inviteClientCode);
            setInviteSuccess(`Invitation envoyée à ${inviteEmail}`);
            setInviteEmail('');
            setInviteClientCode('');
            setShowModal(false);
            fetchMembers();
        } catch (err: any) {
            setInviteError(err.message || "Erreur lors de l'envoi de l'invitation");
        } finally {
            setInviting(false);
        }
    }, [inviteEmail, inviteClientCode, fetchMembers]);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setInviteEmail('');
        setInviteClientCode('');
        setInviteError(null);
    }, []);

    const handleShowClients = useCallback(async (member: MemberUser) => {
        setSelectedMember(member);
        setLoadingClients(true);
        try {
            const res = await getMemberClients(member.id);
            setMemberClients(res.data);
        } catch {
            setMemberClients([]);
        } finally {
            setLoadingClients(false);
        }
    }, []);

    const handleResetPin = useCallback(async (member: MemberUser) => {
        if (!window.confirm(`Réinitialiser le code PIN de ${member.email} ? La protection sera désactivée immédiatement.`)) return;
        setResettingPinId(member.id);
        setError(null);
        try {
            await resetMemberPin(member.id);
            setMembers((previous) => previous.map((item) => item.id === member.id
                ? { ...item, pinEnabled: false, hasPin: false, pinProtectedAreas: [] }
                : item));
            setInviteSuccess(`Code PIN réinitialisé pour ${member.email}`);
        } catch (err: any) {
            setError(err.message || 'Impossible de réinitialiser le code PIN');
        } finally {
            setResettingPinId(null);
        }
    }, []);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Membres</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-base-content/50">{filtered.length} membre{filtered.length !== 1 ? 's' : ''}</span>
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn btn-primary btn-sm gap-2"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Inviter
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error mb-4">
                        <span>{error}</span>
                    </div>
                )}

                {inviteSuccess && (
                    <div className="alert alert-success mb-4">
                        <span>{inviteSuccess}</span>
                    </div>
                )}

                {/* Barre de recherche + lignes par page */}
                <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="relative flex-1 max-w-sm">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            className="input input-bordered input-sm w-full pl-9"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-base-content/50">Lignes :</span>
                        <select
                            className="select select-bordered select-sm"
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                            {ROWS_PER_PAGE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto bg-white rounded-lg border border-base-300">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Prénom</th>
                                <th>Nom</th>
                                <th>Code client</th>
                                <th>Email</th>
                                <th>Date d'inscription</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-base-content/50 py-8">
                                        Aucun membre trouvé
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((member) => (
                                    <tr key={member.id}>
                                        <td>{member.firstName || '-'}</td>
                                        <td>{member.lastName || '-'}</td>
                                        <td>{member.clientCode || '-'}</td>
                                        <td>{member.email}</td>
                                        <td>{new Date(member.createdAt).toLocaleDateString('fr-FR')}</td>
                                        <td>
                                            <div className="flex items-center justify-end gap-1">
                                                {member.pinEnabled && <span className="badge badge-success badge-outline badge-sm">PIN actif</span>}
                                                <button
                                                    onClick={() => handleShowClients(member)}
                                                    className="btn btn-ghost btn-xs gap-1"
                                                >
                                                    <UsersIcon className="w-4 h-4" />
                                                    Clients
                                                </button>
                                                {member.hasPin && (
                                                    <button
                                                        onClick={() => handleResetPin(member)}
                                                        disabled={resettingPinId === member.id}
                                                        className="btn btn-ghost btn-xs gap-1 text-warning"
                                                        title="Réinitialiser le code PIN à zéro"
                                                    >
                                                        {resettingPinId === member.id ? <span className="loading loading-spinner loading-xs" /> : <KeyIcon className="w-4 h-4" />}
                                                        Réinitialiser PIN
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <span className="text-sm text-base-content/50">
                            {(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, filtered.length)} sur {filtered.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                className="btn btn-ghost btn-sm"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                                    if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push('ellipsis');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === 'ellipsis' ? (
                                        <span key={`e-${idx}`} className="px-1 text-base-content/30">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-ghost'}`}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                            <button
                                className="btn btn-ghost btn-sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Invite Modal */}
            <dialog className={`modal ${showModal ? 'modal-open' : ''}`}>
                <div className="modal-box">
                    <h3 className="font-bold text-lg mb-4">Inviter un membre</h3>

                    {inviteError && (
                        <div className="alert alert-error mb-4">
                            <span>{inviteError}</span>
                        </div>
                    )}

                    <form onSubmit={handleInvite}>
                        <div className="form-control w-full mb-4">
                            <label className="label">
                                <span className="label-text">Adresse email</span>
                            </label>
                            <input
                                type="email"
                                placeholder="email@exemple.com"
                                className="input input-bordered w-full"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                required
                                disabled={inviting}
                            />
                        </div>

                        <div className="form-control w-full mb-6">
                            <label className="label">
                                <span className="label-text">Code client</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: CL-00123"
                                className="input input-bordered w-full"
                                value={inviteClientCode}
                                onChange={(e) => setInviteClientCode(e.target.value)}
                                required
                                disabled={inviting}
                            />
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={handleCloseModal}
                                disabled={inviting}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={inviting}
                            >
                                {inviting ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Envoyer l\'invitation'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={handleCloseModal}>close</button>
                </form>
            </dialog>

            {/* Clients Modal */}
            {selectedMember && (
                <dialog className="modal modal-open">
                    <div className="modal-box max-w-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="font-bold text-lg">Clients de {selectedMember.firstName || ''} {selectedMember.lastName || selectedMember.email}</h3>
                                <p className="text-xs text-base-content/50 mt-0.5">{selectedMember.email}</p>
                            </div>
                            <button className="btn btn-ghost btn-sm btn-circle" onClick={() => setSelectedMember(null)}>
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {loadingClients ? (
                            <div className="flex items-center justify-center py-8">
                                <span className="loading loading-spinner loading-md"></span>
                            </div>
                        ) : memberClients.length === 0 ? (
                            <p className="text-center text-base-content/50 py-8">Aucun client pour ce membre</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Prénom</th>
                                            <th>Nom</th>
                                            <th>Email</th>
                                            <th>Téléphone</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {memberClients.map((client) => (
                                            <tr key={client.id}>
                                                <td>{client.firstName || '-'}</td>
                                                <td>{client.lastName || '-'}</td>
                                                <td>{client.email || '-'}</td>
                                                <td>{client.phone || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className="text-xs text-base-content/50 mt-2">{memberClients.length} client{memberClients.length !== 1 ? 's' : ''}</p>
                            </div>
                        )}
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button onClick={() => setSelectedMember(null)}>close</button>
                    </form>
                </dialog>
            )}
        </Layout>
    );
};
